import { SessionLeafNode } from '@biconomy/modules/dist/types/interfaces/ISessionStorage'
import { Period } from '@kwenta/sdk/constants'
import {
	FuturesMarketAsset,
	FuturesMarketKey,
	PositionSide,
	SwapDepositToken,
	TransactionStatus,
} from '@kwenta/sdk/types'
import Wei from '@kwenta/wei'
import { AbstractionToken } from 'services/biconomy/accountAbstraction.types'

import { HistoryTab } from 'sections/dashboard/History/HistoryTabs'
import type { TableFilter } from 'sections/futures/UserInfo/TableFilters/TableFilters'
import { FuturesTab } from 'sections/futures/UserInfo/UserInfoTabs'

import { ClosePositionInputs, FuturesTransactionType, TradePanelInputs } from './common/types'

export enum PerpsProvider {
	SNX_V2 = 'snx_v2',
	SNX_V3 = 'snx_v3',
	PERENNIAL_V2 = 'perennial_v2',
}
export type FuturesAction = {
	account: string
	timestamp: number
	asset: FuturesMarketAsset
	margin: number
	size: number
	action: 'trade' | 'deposit' | 'withdraw'
}

export type PortfolioValues = {
	timestamp: number
	total: number
}

export type FuturesTransaction = {
	type: FuturesTransactionType
	status: TransactionStatus
	error?: string
	hash: string | null
}

export type InputCurrencyDenomination = 'usd' | 'native'

export type FundingRatePeriods = {
	[key: number]: string
}

export enum TradingModes {
	DEFAULT = 'DEFAULT',
	ONE_CLICK = 'ONE_CLICK',
}

export enum ManageModalType {
	Deposit = 0,
	Withdraw = 1,
	Bridge = 2,
	OneClick = 3,
	Delegation = 4,
	Orders = 5,
}

export type SLTPInputs<T = Wei> = {
	stopLossPrice?: T
	takeProfitPrice?: T
}

export type EditPositionInputs<T = Wei> = {
	nativeSizeDelta: T
	marginDelta: T
}

export type FuturesPreferences = {
	selectedManageModalType: ManageModalType
	selectedSwapDepositToken: SwapDepositToken
	selectedInputHours: number
	selectedChart: 'price' | 'funding'
	selectedInputDenomination: InputCurrencyDenomination
	selectedProvider: PerpsProvider
	showHistory?: boolean
	showPositionLines?: boolean
	showOrderLines?: boolean
	showRealizedPnL?: boolean
	showTestnets?: boolean
	showFunding?: boolean
	tradingMode: TradingModes
	userInfoCollapsed: boolean
	userInfoShowAllMarkets: boolean
	historicalFundingRatePeriod: Period
	favoriteMarkets: FuturesMarketAsset[]
	profitCalculatorVisible: boolean
	portfolioChartType: 'account' | 'pnl'
	userInfoTableFilter: {
		[key in TableFilteredFuturesTabs]: TableFilter | null
	}
	dashboardHistoryTableFilter: {
		[key in HistoryTab]: DashboardHistoryTableFilter | null
	}
}

export type FuturesState = {
	selectedMarketAsset: Record<PerpsProvider, FuturesMarketAsset>
	preferences: FuturesPreferences
	oneClickTrading: {
		accountDelegated: boolean
		ethBalance: string
		usdcBalance: string
		usdcEngineAllowance: string
		sessionInfo?: SessionLeafNode
		isSessionActive: boolean
		estTxCost: {
			value: string
			asset: AbstractionToken
		}
	}
	dashboard: {
		selectedPortfolioTimeframe: Period
	}
	leaderboard: {
		selectedTrader?: { trader: string; traderEns?: string | null; accountId?: string }
		pageSize: number
	}
	sltpModalInputs: SLTPInputs<string>
	editPositionInputs: EditPositionInputs<string>
	tradePanelDrawerOpen: boolean
	delegation: {
		selectedDelegationAddress: string
		selectedSubAccountAddress: string
		newNickname: string
		addressBook: Record<string, Array<DelegationAccountInfo>>
	}
	closePositionOrderInputs: ClosePositionInputs<string>
	tradePanel: {
		isDraggingSlider: boolean
		leverageSide: PositionSide
		leverageInput: string
		inputs: TradePanelInputs<string>
	}
}

export type ExecuteDelayedOrderInputs = {
	marketKey: FuturesMarketKey
	marketAddress: string
	originTxHash: `0x${string}`
}

export type DelegationAccountInfo = {
	address: string
	nickname: string
}

export type UIPreferencesPayload = {
	showHistory?: boolean
	showPositionLines?: boolean
	showOrderLines?: boolean
	showRealizedPnL?: boolean
	showTestnets?: boolean
	showFunding?: boolean
	tradingMode?: TradingModes
	userInfoCollapsed?: boolean
	userInfoShowAllMarkets?: boolean
	historicalFundingRatePeriod?: Period
	selectedProvider?: PerpsProvider
	selectedInputDenomination?: InputCurrencyDenomination
	selectedChart?: 'price' | 'funding'
	selectedInputHours?: number
	selectedSwapDepositToken?: SwapDepositToken
	selectedManageModalType?: ManageModalType
	portfolioChartType?: 'account' | 'pnl'
}

export type TableFilteredFuturesTabs = Extract<
	FuturesTab,
	FuturesTab.TRADES | FuturesTab.TRANSACTION_HISTORY | FuturesTab.ORDER_HISTORY
>

export type TradeHistory = {
	market: {
		asset: FuturesMarketAsset
		marketKey: FuturesMarketKey
		marketName: string
	}
	side: PositionSide
	orderType: string
	amount: Wei
	notionalValue: Wei
	interestCharged?: Wei
	pnl: Wei
	netPnl: Wei
	funding: Wei
	price: Wei
	feesPaid: Wei
	txnHash?: string
	displayAsset?: string | null
	timestamp: string | number
}

export type DashboardHistoryTableFilter = {
	asset?: string
	from?: string
	to?: string
	network?: string
	type?: string
	status?: string
	side?: string
	search?: string
}
